import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Printer, GlassWater, Wine as WineIcon } from "lucide-react";
import PaperListView from "@/components/PaperListView";

/* Admin printable catalog.
 *
 * Fetches the FULL public catalog (Listino Pubblico prices) — anonymous role
 * so the admin always sees the exact prices that would print in the paper menu.
 * Falls back to the admin's own token if that endpoint requires auth (not for
 * /api/wines which is public). Distillati sono opzionali (checkbox).
 *
 * The @media print CSS is defined in App.css (.paper-list rules); this page
 * only orchestrates fetching + a "Stampa" button.
 */

export default function AdminPrintCatalog() {
    const [wines, setWines] = useState([]);
    const [spirits, setSpirits] = useState([]);
    const [includeSpirits, setIncludeSpirits] = useState(false);
    const [loading, setLoading] = useState(true);
    const [genDate] = useState(() => new Date().toLocaleDateString("it-IT", {
        day: "numeric", month: "long", year: "numeric",
    }));

    useEffect(() => {
        setLoading(true);
        Promise.all([
            // Fetch public prices (anonymous). Use fetch to avoid the axios
            // interceptor injecting the admin bearer (would trigger staff role
            // response with different serialization).
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wines?limit=100&skip=0`).then((r) => r.json()),
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wines?limit=100&skip=0&categoria=non_vino`).then((r) => r.json()),
        ]).then(async ([w0, s0]) => {
            const all = [...(w0.items || [])];
            const totalW = w0.total || 0;
            for (let skip = 100; skip < totalW; skip += 100) {
                const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wines?limit=100&skip=${skip}`).then((r) => r.json());
                all.push(...(r.items || []));
            }
            setWines(all);
            const allS = [...(s0.items || [])];
            const totalS = s0.total || 0;
            for (let skip = 100; skip < totalS; skip += 100) {
                const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wines?limit=100&skip=${skip}&categoria=non_vino`).then((r) => r.json());
                allS.push(...(r.items || []));
            }
            setSpirits(allS);
        }).finally(() => setLoading(false));
    }, []);

    const doPrint = () => window.print();

    return (
        <div className="co-container py-6" data-testid="admin-print-catalog-page">
            <div className="print:hidden mb-6 flex flex-col sm:flex-row sm:items-baseline gap-3 sm:justify-between">
                <div>
                    <span className="co-caption block mb-1">Amministrazione</span>
                    <h1 className="text-3xl sm:text-4xl">Stampa carta dei vini</h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-xl">
                        Genera la versione stampabile completa con i prezzi del Listino Pubblico.
                        Attiva le formattazioni di stampa dal dialogo del browser (Ctrl/Cmd + P).
                    </p>
                </div>
                <div className="flex flex-col sm:items-end gap-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="admin-print-include-spirits">
                        <input
                            type="checkbox"
                            checked={includeSpirits}
                            onChange={(e) => setIncludeSpirits(e.target.checked)}
                            className="accent-[hsl(var(--primary))]"
                        />
                        Includi Distillati &amp; Liquori come sezione finale
                    </label>
                    <button
                        onClick={doPrint}
                        className="co-btn-primary text-sm"
                        data-testid="admin-print-btn"
                        disabled={loading}
                    >
                        <Printer className="w-4 h-4" strokeWidth={1.5} /> Stampa
                    </button>
                </div>
            </div>

            <div className="paper-print-shell">
                <header className="paper-print-header text-center py-8 border-b border-[hsl(var(--border))] mb-8">
                    <div className="paper-print-logo mx-auto mb-4 w-8 h-8">
                        {/* Small acorn glyph */}
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-[hsl(var(--primary))]">
                            <path d="M6 8h12l-1.2 3.5A6 6 0 0 1 11 15.6 6 6 0 0 1 7.2 11.5L6 8z" stroke="currentColor" strokeWidth="1.2"/>
                            <path d="M12 8V4M9 4h6" stroke="currentColor" strokeWidth="1.2"/>
                        </svg>
                    </div>
                    <p className="paper-print-eyebrow tracking-[0.3em] text-xs uppercase text-[hsl(var(--muted-foreground))]">Casa Orterie · Stazzona</p>
                    <h1 className="paper-print-title text-4xl font-serif italic mt-2">Carta dei Vini</h1>
                    <p className="paper-print-date text-xs text-[hsl(var(--muted-foreground))] mt-3">Aggiornata al {genDate}</p>
                </header>

                {loading && (
                    <div className="py-16 text-center text-[hsl(var(--muted-foreground))] italic">
                        Caricamento del catalogo… ({wines.length} vini finora)
                    </div>
                )}

                {!loading && (
                    <>
                        <div data-testid="admin-print-wines-block">
                            <PaperListView
                                wines={wines}
                                canSeePrices={true}
                                noticeForNoPrice={null}
                            />
                        </div>

                        {includeSpirits && spirits.length > 0 && (
                            <section className="paper-print-spirits mt-16 pt-8 border-t-2 border-[hsl(var(--border))]" data-testid="admin-print-spirits-block">
                                <header className="text-center mb-8">
                                    <p className="tracking-[0.3em] text-xs uppercase text-[hsl(var(--muted-foreground))]">Sezione finale</p>
                                    <h2 className="text-3xl font-serif italic mt-2">
                                        <GlassWater className="w-6 h-6 inline mr-2 opacity-60" strokeWidth={1.2}/>
                                        Distillati &amp; Liquori
                                    </h2>
                                </header>
                                <PaperListView
                                    wines={spirits}
                                    canSeePrices={true}
                                    noticeForNoPrice={null}
                                />
                            </section>
                        )}

                        <footer className="paper-print-footer mt-16 text-center text-xs text-[hsl(var(--muted-foreground))]">
                            <WineIcon className="w-3 h-3 inline mr-1" strokeWidth={1.5}/>
                            Casa Orterie · Stazzona · I prezzi si intendono in
                            euro, IVA inclusa.
                        </footer>
                    </>
                )}
            </div>
        </div>
    );
}
